import { useState, useMemo, useRef } from 'react';

/* ── CONSTANTS ──────────────────────────────────────────── */
const FILMFREEWAY_PCT = 0.12;
const STRIPE_PCT = 0.029;
const STRIPE_FIXED = 0.30;
const STRIPE_PAYOUT_PCT = 0.0025;
const SCENE_AVG = 17, SHORT_AVG = 23, FEAT_AVG = 28, ADDON_PRICE = 10;
const T_GEN = 25, T_VIP = 50, T_ALL = 75;
const S_FOUND = 500, S_COMM = 1500, S_CAT = 3500, S_PRES = 10000;
const PRIZES = 12000;
const AWS_BASE = 850;
const AWS_PER_100_VIEWERS = 210;
const MISC = 400;

const SCENARIOS = {
  pessimistic: {
    label: 'Pessimistic',
    desc: 'First festival, limited outreach, no big sponsors',
    scene: 100, short: 150, feat: 80, addon: 30,
    tGen: 150, tVip: 50, tAll: 20,
    sFound: 10, sComm: 3, sCat: 2, sPres: 0,
    adSpend: 1000, adMon: 3,
  },
  realistic: {
    label: 'Realistic',
    desc: 'Solid execution, good FilmFreeway visibility, a few key sponsors',
    scene: 200, short: 350, feat: 200, addon: 100,
    tGen: 500, tVip: 200, tAll: 80,
    sFound: 25, sComm: 10, sCat: 6, sPres: 1,
    adSpend: 1000, adMon: 3,
  },
  optimistic: {
    label: 'Optimistic',
    desc: 'Strong marketing, viral reach, full sponsor roster',
    scene: 400, short: 600, feat: 350, addon: 250,
    tGen: 1500, tVip: 600, tAll: 200,
    sFound: 50, sComm: 20, sCat: 12, sPres: 2,
    adSpend: 1000, adMon: 3,
  },
};

/* ── HELPERS ────────────────────────────────────────────── */
function fmt(n) {
  const abs = Math.abs(Math.round(n));
  const s = '$' + abs.toLocaleString();
  return n < 0 ? '-' + s : s;
}
function fmtPct(n) { return Math.round(n) + '%'; }

function computeScenario(s) {
  const grossSub = (s.scene * SCENE_AVG) + (s.short * SHORT_AVG) + (s.feat * FEAT_AVG) + (s.addon * ADDON_PRICE);
  const grossTickets = (s.tGen * T_GEN) + (s.tVip * T_VIP) + (s.tAll * T_ALL);
  const grossSponsors = (s.sFound * S_FOUND) + (s.sComm * S_COMM) + (s.sCat * S_CAT) + (s.sPres * S_PRES);
  const grossRevenue = grossSub + grossTickets + grossSponsors;
  const totalSubs = s.scene + s.short + s.feat;

  const ffFee = grossSub * FILMFREEWAY_PCT;
  const totalTicketTx = s.tGen + s.tVip + s.tAll;
  const stripeFee = (grossTickets * STRIPE_PCT) + (totalTicketTx * STRIPE_FIXED);
  const affiliateCommission = (grossTickets * 0.20 * 0.10) + (grossSub * 0.10 * 0.08);
  const stripePayoutFee = affiliateCommission * STRIPE_PAYOUT_PCT;
  const totalViewers = s.tGen + s.tVip + s.tAll;
  const awsCost = AWS_BASE + (Math.floor(totalViewers / 100) * AWS_PER_100_VIEWERS);
  const adTotal = s.adSpend * s.adMon;
  const totalExpenses = ffFee + stripeFee + stripePayoutFee + awsCost + adTotal + PRIZES + affiliateCommission + MISC;
  const netProfit = grossRevenue - totalExpenses;
  const margin = grossRevenue > 0 ? (netProfit / grossRevenue) * 100 : 0;

  return { grossSub, grossTickets, grossSponsors, grossRevenue, ffFee, stripeFee,
           affiliateCommission, awsCost, adTotal, totalExpenses, netProfit, margin,
           totalSubs, totalViewers };
}

/* ── DEFAULTS (Realistic) ──────────────────────────────── */
const D = SCENARIOS.realistic;

/* ── COMPONENT ─────────────────────────────────────────── */
export default function ProfitCalculator() {
  const topRef = useRef(null);

  // Submission sliders
  const [scene, setScene] = useState(D.scene);
  const [short, setShort] = useState(D.short);
  const [feat, setFeat] = useState(D.feat);
  const [addon, setAddon] = useState(D.addon);

  // Ticket sliders
  const [tGen, setTGen] = useState(D.tGen);
  const [tVip, setTVip] = useState(D.tVip);
  const [tAll, setTAll] = useState(D.tAll);

  // Sponsor sliders
  const [sFound, setSFound] = useState(D.sFound);
  const [sComm, setSComm] = useState(D.sComm);
  const [sCat, setSCat] = useState(D.sCat);
  const [sPres, setSPres] = useState(D.sPres);

  // Advertising
  const [adSpend, setAdSpend] = useState(D.adSpend);
  const [adMon, setAdMon] = useState(D.adMon);

  /* ── COMPUTED VALUES ──────────────────────────────────── */
  const calc = useMemo(() => {
    const subScene = scene * SCENE_AVG;
    const subShort = short * SHORT_AVG;
    const subFeat = feat * FEAT_AVG;
    const subAddon = addon * ADDON_PRICE;
    const grossSub = subScene + subShort + subFeat + subAddon;

    const ticketGen = tGen * T_GEN;
    const ticketVip = tVip * T_VIP;
    const ticketAll = tAll * T_ALL;
    const grossTickets = ticketGen + ticketVip + ticketAll;

    const sponsorFound = sFound * S_FOUND;
    const sponsorComm = sComm * S_COMM;
    const sponsorCat = sCat * S_CAT;
    const sponsorPres = sPres * S_PRES;
    const grossSponsors = sponsorFound + sponsorComm + sponsorCat + sponsorPres;

    const grossRevenue = grossSub + grossTickets + grossSponsors;

    const ffFee = grossSub * FILMFREEWAY_PCT;
    const totalTicketTx = tGen + tVip + tAll;
    const stripeFee = (grossTickets * STRIPE_PCT) + (totalTicketTx * STRIPE_FIXED);
    const affiliateTicketCommission = grossTickets * 0.20 * 0.10;
    const affiliateSubCommission = grossSub * 0.10 * 0.08;
    const totalAffiliate = affiliateTicketCommission + affiliateSubCommission;
    const stripePayoutFee = totalAffiliate * STRIPE_PAYOUT_PCT;
    const totalViewers = tGen + tVip + tAll;
    const awsCost = AWS_BASE + (Math.floor(totalViewers / 100) * AWS_PER_100_VIEWERS);
    const adTotal = adSpend * adMon;
    const prizes = PRIZES;
    const misc = MISC;

    const totalExpenses = ffFee + stripeFee + stripePayoutFee + awsCost + adTotal + prizes + totalAffiliate + misc;
    const netProfit = grossRevenue - totalExpenses;
    const margin = grossRevenue > 0 ? (netProfit / grossRevenue) * 100 : 0;
    const expRatio = grossRevenue > 0 ? (totalExpenses / grossRevenue) * 100 : 100;

    const beSponsor = grossRevenue > 0 ? Math.ceil(totalExpenses / S_FOUND) : null;
    const beTicket = grossRevenue > 0 ? Math.ceil(totalExpenses / T_GEN) : null;

    // P&L rows
    const rows = [
      { type: 'section', label: 'REVENUE' },
      { label: `Scene submissions (${scene} \u00d7 $${SCENE_AVG} avg)`, val: subScene, pos: true },
      { label: `Short/Vertical submissions (${short} \u00d7 $${SHORT_AVG} avg)`, val: subShort, pos: true },
      { label: `Feature/Doc submissions (${feat} \u00d7 $${FEAT_AVG} avg)`, val: subFeat, pos: true },
      { label: `Scene add-ons (${addon} \u00d7 $10)`, val: subAddon, pos: true },
      { type: 'subtotal', label: 'Gross Submissions', val: grossSub },
      { label: `General tickets (${tGen} \u00d7 $25)`, val: ticketGen, pos: true },
      { label: `VIP tickets (${tVip} \u00d7 $50)`, val: ticketVip, pos: true },
      { label: `All-Access tickets (${tAll} \u00d7 $75)`, val: ticketAll, pos: true },
      { type: 'subtotal', label: 'Gross Ticket Sales', val: grossTickets },
      { label: `Founding sponsors (${sFound} \u00d7 $500)`, val: sponsorFound, pos: true },
      { label: `Community sponsors (${sComm} \u00d7 $1,500)`, val: sponsorComm, pos: true },
      { label: `Category sponsors (${sCat} \u00d7 $3,500)`, val: sponsorCat, pos: true },
      { label: `Presenting sponsors (${sPres} \u00d7 $10,000)`, val: sponsorPres, pos: true },
      { type: 'subtotal', label: 'Gross Sponsorships', val: grossSponsors },
      { type: 'total', label: 'GROSS REVENUE', val: grossRevenue },
      { type: 'section', label: 'EXPENSES' },
      { label: 'FilmFreeway fees (12% of submissions)', val: -ffFee },
      { label: 'Stripe processing (2.9% + $0.30/tx on tickets)', val: -stripeFee },
      { label: 'Stripe Connect payouts (affiliate commissions)', val: -totalAffiliate },
      { label: 'Stripe payout processing fee (0.25%)', val: -stripePayoutFee },
      { label: `AWS infrastructure (${totalViewers} viewers)`, val: -awsCost },
      { label: `Advertising (${adMon} mo \u00d7 $${adSpend.toLocaleString()})`, val: -adTotal },
      { label: 'Cash prizes (17 categories)', val: -prizes },
      { label: 'Misc (email tools, design, judges)', val: -misc },
      { type: 'total', label: 'TOTAL EXPENSES', val: -totalExpenses },
      { type: 'net', label: 'NET PROFIT', val: netProfit },
    ];

    // Submissions break-even note
    const fixedCosts = PRIZES + AWS_BASE + (1000 * 3) + MISC;
    const blendedNet = SCENE_AVG * 0.20 * (1 - FILMFREEWAY_PCT) +
                       SHORT_AVG * 0.45 * (1 - FILMFREEWAY_PCT) +
                       FEAT_AVG  * 0.35 * (1 - FILMFREEWAY_PCT);
    const beSubmissions = Math.ceil(fixedCosts / blendedNet);
    const beSubmissionsWithSponsors = Math.ceil((fixedCosts - 5000) / blendedNet);

    return {
      grossRevenue, totalExpenses, netProfit, margin, expRatio,
      beSponsor, beTicket, rows, totalViewers,
      beSubmissions, blendedNet, beSubmissionsWithSponsors,
    };
  }, [scene, short, feat, addon, tGen, tVip, tAll, sFound, sComm, sCat, sPres, adSpend, adMon]);

  /* ── LOAD SCENARIO ────────────────────────────────────── */
  function loadScenario(key) {
    const s = SCENARIOS[key];
    setScene(s.scene);
    setShort(s.short);
    setFeat(s.feat);
    setAddon(s.addon);
    setTGen(s.tGen);
    setTVip(s.tVip);
    setTAll(s.tAll);
    setSFound(s.sFound);
    setSComm(s.sComm);
    setSCat(s.sCat);
    setSPres(s.sPres);
    setAdSpend(s.adSpend);
    setAdMon(s.adMon);
    topRef.current?.scrollIntoView({ behavior: 'smooth' });
  }

  /* ── SCENARIO CARDS (pre-computed) ────────────────────── */
  const scenarioData = useMemo(() => {
    return ['pessimistic', 'realistic', 'optimistic'].map(key => {
      const s = SCENARIOS[key];
      const r = computeScenario(s);
      return { key, ...s, ...r };
    });
  }, []);

  /* ── SLIDER ROW HELPER ────────────────────────────────── */
  function SliderRow({ label, sub, value, setValue, min, max }) {
    return (
      <div className="flex justify-between items-center py-2 border-b border-white/[0.04] last:border-b-0">
        <div className="text-sm text-white/65">
          {label}
          {sub && <small className="block text-xs text-muted mt-px">{sub}</small>}
        </div>
        <div className="flex items-center gap-2">
          <input
            type="range"
            min={min}
            max={max}
            value={value}
            onChange={e => setValue(+e.target.value)}
            className="w-[90px] accent-teal"
          />
          <span className="font-mono text-sm text-white min-w-[55px] text-right">{value}</span>
        </div>
      </div>
    );
  }

  /* ── RENDER ───────────────────────────────────────────── */
  return (
    <div ref={topRef} className="max-w-[1100px] mx-auto p-8">
      {/* HEADER */}
      <h1 className="font-display text-5xl tracking-wider bg-gradient-to-r from-teal to-orange bg-clip-text text-transparent mb-1">
        Synema P&L Calculator
      </h1>
      <div className="font-mono text-xs tracking-[0.15em] uppercase text-muted mb-8">
        Synema Interactive Film Festival &middot; January 21–24, 2027
      </div>

      {/* SUMMARY CARDS */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <div className="bg-dark2 border border-white/[0.06] rounded-md p-5 text-center">
          <div className="font-mono text-[0.65rem] tracking-[0.15em] uppercase text-muted mb-2">Gross Revenue</div>
          <div className="font-display text-4xl tracking-wide leading-none text-teal">{fmt(calc.grossRevenue)}</div>
        </div>
        <div className="bg-dark2 border border-white/[0.06] rounded-md p-5 text-center">
          <div className="font-mono text-[0.65rem] tracking-[0.15em] uppercase text-muted mb-2">Total Expenses</div>
          <div className="font-display text-4xl tracking-wide leading-none text-orange">{fmt(calc.totalExpenses)}</div>
        </div>
        <div className={`bg-dark2 rounded-md p-5 text-center border ${calc.netProfit >= 0 ? 'border-[rgba(76,175,125,0.3)] bg-[rgba(76,175,125,0.05)]' : 'border-[rgba(224,85,85,0.3)] bg-[rgba(224,85,85,0.05)]'}`}>
          <div className="font-mono text-[0.65rem] tracking-[0.15em] uppercase text-muted mb-2">Net Profit</div>
          <div className={`font-display text-4xl tracking-wide leading-none ${calc.netProfit >= 0 ? 'text-[#4CAF7D]' : 'text-[#E05555]'}`}>{fmt(calc.netProfit)}</div>
        </div>
        <div className="bg-dark2 border border-white/[0.06] rounded-md p-5 text-center">
          <div className="font-mono text-[0.65rem] tracking-[0.15em] uppercase text-muted mb-2">Profit Margin</div>
          <div className={`font-display text-4xl tracking-wide leading-none ${calc.margin >= 30 ? 'text-[#4CAF7D]' : calc.margin >= 0 ? 'text-gold' : 'text-[#E05555]'}`}>{fmtPct(calc.margin)}</div>
        </div>
      </div>

      {/* MAIN GRID: INPUTS + P&L TABLE */}
      <div className="grid grid-cols-2 gap-6 mb-6">
        {/* LEFT: INPUTS */}
        <div className="space-y-4">
          {/* Film Submissions */}
          <div className="bg-dark2 border border-white/[0.06] rounded-md p-6">
            <div className="font-mono text-[0.65rem] tracking-[0.2em] uppercase text-teal mb-5">Film Submissions</div>
            <SliderRow label="Scene submissions" sub="$15 early / $20 reg / $25 late avg $17" value={scene} setValue={setScene} min={0} max={500} />
            <SliderRow label="Short / Vertical" sub="$20 early / $25 reg / $30 late avg $23" value={short} setValue={setShort} min={0} max={600} />
            <SliderRow label="Feature / Documentary" sub="$25 early / $30 reg / $35 late avg $28" value={feat} setValue={setFeat} min={0} max={400} />
            <SliderRow label="Scene add-ons" sub="$10 per Short/Feature upsell" value={addon} setValue={setAddon} min={0} max={300} />
          </div>

          {/* Ticket Sales */}
          <div className="bg-dark2 border border-white/[0.06] rounded-md p-6">
            <div className="font-mono text-[0.65rem] tracking-[0.2em] uppercase text-teal mb-5">Ticket Sales</div>
            <SliderRow label="General passes @ $25" value={tGen} setValue={setTGen} min={0} max={2000} />
            <SliderRow label="VIP passes @ $50" value={tVip} setValue={setTVip} min={0} max={1000} />
            <SliderRow label="All-Access passes @ $75" value={tAll} setValue={setTAll} min={0} max={500} />
          </div>

          {/* Sponsorships */}
          <div className="bg-dark2 border border-white/[0.06] rounded-md p-6">
            <div className="font-mono text-[0.65rem] tracking-[0.2em] uppercase text-teal mb-5">Sponsorships</div>
            <SliderRow label="Founding sponsors @ $500" value={sFound} setValue={setSFound} min={0} max={100} />
            <SliderRow label="Community sponsors @ $1,500" value={sComm} setValue={setSComm} min={0} max={50} />
            <SliderRow label="Category sponsors @ $3,500" value={sCat} setValue={setSCat} min={0} max={17} />
            <SliderRow label="Presenting sponsors @ $10,000" value={sPres} setValue={setSPres} min={0} max={2} />
          </div>

          {/* Advertising */}
          <div className="bg-dark2 border border-white/[0.06] rounded-md p-6">
            <div className="font-mono text-[0.65rem] tracking-[0.2em] uppercase text-teal mb-5">Advertising Budget (Monthly x Months)</div>
            <div className="flex justify-between items-center py-2 border-b border-white/[0.04]">
              <div className="text-sm text-white/65">Monthly ad spend</div>
              <div className="flex items-center gap-1">
                <span className="text-white/65">$</span>
                <input
                  type="number"
                  value={adSpend}
                  onChange={e => setAdSpend(+e.target.value || 0)}
                  className="w-[70px] bg-dark3 border border-white/10 rounded-sm text-white font-mono text-sm px-2 py-1 text-right focus:outline-none focus:border-teal"
                />
              </div>
            </div>
            <SliderRow label="Months of advertising" value={adMon} setValue={setAdMon} min={1} max={6} />
          </div>
        </div>

        {/* RIGHT: P&L TABLE */}
        <div className="bg-dark2 border border-white/[0.06] rounded-md p-6">
          <div className="font-mono text-[0.65rem] tracking-[0.2em] uppercase text-teal mb-5">Full P&L Breakdown</div>
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr>
                <th className="font-mono text-[0.62rem] tracking-[0.15em] uppercase text-muted py-2 px-3 text-left border-b border-white/[0.08]">Line Item</th>
                <th className="font-mono text-[0.62rem] tracking-[0.15em] uppercase text-muted py-2 px-3 text-right border-b border-white/[0.08]">Amount</th>
              </tr>
            </thead>
            <tbody>
              {calc.rows.map((r, i) => {
                if (r.type === 'section') {
                  return (
                    <tr key={i} className="bg-dark3">
                      <td colSpan={2} className="text-teal text-[0.7rem] tracking-[0.12em] uppercase font-mono py-1.5 px-3">{r.label}</td>
                    </tr>
                  );
                }
                if (r.type === 'subtotal') {
                  return (
                    <tr key={i} className="bg-white/[0.02]">
                      <td className="py-2 px-3 text-white font-medium">{r.label}</td>
                      <td className="py-2 px-3 text-right font-mono text-[#4CAF7D]">{fmt(r.val)}</td>
                    </tr>
                  );
                }
                if (r.type === 'total') {
                  const cls = r.val >= 0 ? 'text-[#4CAF7D]' : 'text-[#E05555]';
                  return (
                    <tr key={i} className="border-t-2 border-white/[0.12]">
                      <td className="py-3 px-3 text-white font-medium text-[0.95rem]">{r.label}</td>
                      <td className={`py-3 px-3 text-right font-mono text-[0.95rem] font-medium ${cls}`}>{fmt(Math.abs(r.val))}</td>
                    </tr>
                  );
                }
                if (r.type === 'net') {
                  const cls = r.val >= 0 ? 'text-[#4CAF7D]' : 'text-[#E05555]';
                  return (
                    <tr key={i} className="border-t-2 border-[rgba(76,175,125,0.4)]">
                      <td className={`py-3 px-3 font-display text-xl tracking-wide ${cls}`}>{r.label}</td>
                      <td className={`py-3 px-3 text-right font-display text-xl tracking-wide ${cls}`}>{fmt(r.val)}</td>
                    </tr>
                  );
                }
                // Regular row
                const cls = r.val >= 0 ? 'text-[#4CAF7D]' : r.val < 0 ? 'text-[#E05555]' : '';
                return (
                  <tr key={i} className="border-b border-white/[0.04]">
                    <td className="py-2 px-3 text-white/70">{r.label}</td>
                    <td className={`py-2 px-3 text-right font-mono ${cls}`}>{fmt(r.val)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {/* Margin bar */}
          <div className="mt-4">
            <div className="flex justify-between text-xs text-muted font-mono mb-1.5">
              <span>Expense ratio</span>
              <span>{fmtPct(calc.expRatio)}</span>
            </div>
            <div className="h-2 bg-dark3 rounded-full overflow-hidden">
              <div
                className="h-full rounded-full transition-all duration-300"
                style={{
                  width: Math.min(100, calc.expRatio) + '%',
                  background: calc.expRatio < 50 ? '#4CAF7D' : calc.expRatio < 75 ? '#D4A843' : '#E05555',
                }}
              />
            </div>
          </div>

          {/* Breakeven indicator */}
          <div className={`flex items-center justify-between p-4 rounded mt-4 ${
            calc.netProfit >= 0
              ? 'bg-[rgba(76,175,125,0.1)] border border-[rgba(76,175,125,0.2)]'
              : calc.netProfit > -10000
                ? 'bg-[rgba(212,168,67,0.1)] border border-[rgba(212,168,67,0.2)]'
                : 'bg-[rgba(224,85,85,0.1)] border border-[rgba(224,85,85,0.2)]'
          }`}>
            <div>
              <div className="font-mono text-xs text-muted tracking-[0.1em] uppercase">Sponsors needed to break even</div>
              <div className="font-display text-2xl tracking-wide">
                {calc.beSponsor != null ? `${calc.beSponsor} @ $500` : '\u2014'}
              </div>
            </div>
            <div>
              <div className="font-mono text-xs text-muted tracking-[0.1em] uppercase">Tickets needed to break even</div>
              <div className="font-display text-2xl tracking-wide">
                {calc.beTicket != null ? `${calc.beTicket} @ $25` : '\u2014'}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── SCENARIO COMPARISON ──────────────────────────── */}
      <div className="mt-10">
        <div className="flex items-baseline gap-4 mb-5">
          <div className="font-display text-3xl tracking-wide text-white">Scenario Comparison</div>
          <div className="font-mono text-xs tracking-[0.12em] uppercase text-muted">Three cases &middot; Click any to load into calculator</div>
        </div>

        <div className="grid grid-cols-3 gap-5">
          {scenarioData.map(sc => {
            const key = sc.key;
            const profitCls = sc.netProfit >= 0 ? 'text-[#4CAF7D]' : 'text-[#E05555]';

            const borderColor = key === 'pessimistic'
              ? 'border-[rgba(224,85,85,0.25)]'
              : key === 'realistic'
                ? 'border-[rgba(212,168,67,0.3)]'
                : 'border-[rgba(76,175,125,0.3)]';

            const headBg = key === 'pessimistic'
              ? 'bg-[rgba(224,85,85,0.08)]'
              : key === 'realistic'
                ? 'bg-[rgba(212,168,67,0.08)]'
                : 'bg-[rgba(76,175,125,0.08)]';

            const badgeColor = key === 'pessimistic'
              ? 'text-[#E05555]'
              : key === 'realistic'
                ? 'text-gold'
                : 'text-[#4CAF7D]';

            const profitColor = key === 'pessimistic'
              ? 'text-[#E05555]'
              : key === 'realistic'
                ? 'text-gold'
                : 'text-[#4CAF7D]';

            const btnClasses = key === 'pessimistic'
              ? 'bg-[rgba(224,85,85,0.12)] text-[#E05555] border border-[rgba(224,85,85,0.25)] hover:bg-[rgba(224,85,85,0.22)]'
              : key === 'realistic'
                ? 'bg-[rgba(212,168,67,0.12)] text-gold border border-[rgba(212,168,67,0.25)] hover:bg-[rgba(212,168,67,0.22)]'
                : 'bg-[rgba(76,175,125,0.12)] text-[#4CAF7D] border border-[rgba(76,175,125,0.25)] hover:bg-[rgba(76,175,125,0.22)]';

            return (
              <div key={key} className={`rounded-md overflow-hidden border ${borderColor}`}>
                {/* Head */}
                <div className={`p-5 ${headBg}`}>
                  <div className={`font-mono text-[0.62rem] tracking-[0.2em] uppercase mb-1 ${badgeColor}`}>{key.toUpperCase()}</div>
                  <div className="font-display text-2xl tracking-wide text-white">{sc.label}</div>
                  <div className="text-xs text-muted mt-0.5 leading-snug">{sc.desc}</div>
                  <div className={`font-display text-5xl tracking-tight mt-3 leading-none ${profitColor}`}>{fmt(sc.netProfit)}</div>
                  <div className="text-xs text-muted font-mono mt-0.5">
                    Margin: {fmtPct(sc.margin)} &nbsp;&middot;&nbsp; {sc.totalSubs} submissions &nbsp;&middot;&nbsp; {sc.totalViewers} viewers
                  </div>
                </div>

                {/* Body */}
                <div className="bg-dark2 px-6 py-4">
                  <ScRow label="Submission revenue" val={fmt(sc.grossSub)} cls="text-[#4CAF7D]" />
                  <ScRow label="Ticket revenue" val={fmt(sc.grossTickets)} cls="text-[#4CAF7D]" />
                  <ScRow label="Sponsor revenue" val={fmt(sc.grossSponsors)} cls="text-[#4CAF7D]" />
                  <div className="flex justify-between items-center py-1.5 border-b border-white/[0.04] font-medium">
                    <span className="text-white text-sm">Gross revenue</span>
                    <span className="font-mono text-xs text-[#4CAF7D]">{fmt(sc.grossRevenue)}</span>
                  </div>
                  <div className="h-px bg-white/[0.06] my-2" />
                  <ScRow label="FilmFreeway (12%)" val={fmt(-sc.ffFee)} cls="text-[#E05555]" />
                  <ScRow label="Stripe fees" val={fmt(-sc.stripeFee)} cls="text-[#E05555]" />
                  <ScRow label="Affiliate commissions" val={fmt(-sc.affiliateCommission)} cls="text-[#E05555]" />
                  <ScRow label="AWS infrastructure" val={fmt(-sc.awsCost)} cls="text-[#E05555]" />
                  <ScRow label="Advertising (3 mo)" val={fmt(-sc.adTotal)} cls="text-[#E05555]" />
                  <ScRow label="Cash prizes" val={fmt(-PRIZES)} cls="text-[#E05555]" />
                  <ScRow label="Misc" val={fmt(-MISC)} cls="text-[#E05555]" />
                  <div className="flex justify-between items-center py-1.5 border-b border-white/[0.04] font-medium">
                    <span className="text-white text-sm">Total expenses</span>
                    <span className="font-mono text-xs text-[#E05555]">{fmt(-sc.totalExpenses)}</span>
                  </div>
                  <div className="h-px bg-white/[0.06] my-2" />
                  <div className="flex justify-between items-center py-1.5 font-medium text-[0.88rem]">
                    <span className="text-white">Net profit</span>
                    <span className={`font-mono text-base ${profitCls}`}>{fmt(sc.netProfit)}</span>
                  </div>
                </div>

                {/* Load button */}
                <button
                  onClick={() => loadScenario(key)}
                  className={`block w-[calc(100%-3rem)] mx-6 my-4 py-2.5 rounded-sm font-mono text-[0.7rem] tracking-[0.12em] uppercase cursor-pointer transition-all duration-150 ${btnClasses}`}
                >
                  Load into calculator &rarr;
                </button>
              </div>
            );
          })}
        </div>

        {/* Submissions break-even note */}
        <div className="mt-5 p-4 bg-dark3 rounded border-l-[3px] border-l-teal text-sm text-white/60 leading-relaxed">
          <strong className="text-teal">Submissions-only break-even: ~{calc.beSubmissions} submissions</strong> at a blended net of ~${calc.blendedNet.toFixed(2)}/submission after FilmFreeway fees.
          That assumes zero tickets and zero sponsors. With just 10 Founding sponsors ($5,000) the break-even drops to ~{calc.beSubmissionsWithSponsors} submissions.
          With 25 sponsors at mixed tiers, submissions become pure profit from submission #1.
        </div>
      </div>
    </div>
  );
}

/* ── SMALL HELPERS ──────────────────────────────────────── */
function ScRow({ label, val, cls }) {
  return (
    <div className="flex justify-between items-center py-1.5 border-b border-white/[0.04] text-sm">
      <span className="text-white/55">{label}</span>
      <span className={`font-mono text-xs ${cls}`}>{val}</span>
    </div>
  );
}
